/*
Radio images and MP3s: which files the uploader takes, what it names them, and how a
stored URL is previewed.

Uploads follow the MAM uploader (components/Views/Mam/Uploader.jsx): a random
`<20 base36 chars>_<Date.now()>.<ext>` under psweb/ in the assets bucket, served from
https://assets.primaverasound.com/psweb/<name>.

Legacy values are protocol-relative (`//assets.primaverasound.com/2019/...`, pspanel
stored them that way). The form shows them with `https:` in front for the preview only;
the stored value is never rewritten.

CommonJS so radioAssets.test.js runs under plain node, like cmsTabs.js.
*/

const ASSETS_BASE_URL = 'https://assets.primaverasound.com/psweb/';

const UPLOAD_KINDS = {
  image: {
    extensions: ['jpg', 'jpeg', 'png', 'gif'],
    // react-dropzone 14 `accept`: matches on the type or the extension.
    accept: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/gif': ['.gif'] },
    defaultContentType: 'image/jpeg',
  },
  mp3: {
    extensions: ['mp3'],
    accept: { 'audio/mpeg': ['.mp3'] },
    defaultContentType: 'audio/mpeg',
  },
};

const fileExtension = (name) => {
  const base = String(name || '');
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
};

const isAcceptedFile = (kind, name) => {
  const config = UPLOAD_KINDS[kind];
  return Boolean(config) && config.extensions.includes(fileExtension(name));
};

/*
The Content-Type to presign and PUT with. Both must be the same string (the presigned URL
signs it), and some browsers give an MP3 an empty or odd type, so fall back per kind.
*/
const uploadContentType = (kind, file) => {
  const config = UPLOAD_KINDS[kind];
  if (file && file.type) return file.type;
  return config ? config.defaultContentType : 'application/octet-stream';
};

const randomBase36 = (length, random = Math.random) => Array.from({ length })
  .map(() => Math.floor(random() * 36).toString(36))
  .join('');

// { filename, extension, key, url } for a new upload. `random` and `now` are injectable for tests.
const buildUploadName = (originalName, { random = Math.random, now = Date.now() } = {}) => {
  const extension = fileExtension(originalName);
  const filename = `${randomBase36(20, random)}_${now}`;
  const key = `${filename}.${extension}`;
  return {
    filename, extension, key, url: `${ASSETS_BASE_URL}${key}`,
  };
};

// The name a person gave the file, without the extension (MAM's readable name).
const readableName = (originalName) => {
  const base = String(originalName || '');
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(0, dot) : base;
};

// For <img src> / <audio src> only. Never store the result.
const previewUrl = (value) => {
  const url = String(value || '').trim();
  if (!url) return '';
  return url.startsWith('//') ? `https:${url}` : url;
};

// "104.9 MB" for the progress line.
const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

module.exports = {
  ASSETS_BASE_URL,
  UPLOAD_KINDS,
  fileExtension,
  isAcceptedFile,
  uploadContentType,
  buildUploadName,
  readableName,
  previewUrl,
  formatBytes,
};
